import { isRichTextEmpty } from '../../../../utils/richText';
import {
  getCorporatePagePositionOptions,
  PAGE_CONTENT_PLACEMENT_LABELS,
  isPageContentPlacement
} from '../contentCards/pageContentPlacements';
import {
  DEFAULT_CONTACT_SPLIT_SETTINGS,
  DEFAULT_FAQ_SECTION_SETTINGS,
  type FaqContactItem,
  type FaqSectionCategory,
  type FaqSectionItem,
  type FaqSectionPayload,
  type FaqSectionSettings,
  type FaqSectionTargetPage,
  type FaqSectionTemplate
} from './types';

export const normalizeFaqSectionTemplate = (value: unknown): FaqSectionTemplate => {
  if (value === 'tabs') return 'tabs';
  if (value === 'contact-split') return 'contact-split';
  return 'accordion';
};

export const getFaqSectionTemplateLabel = (template: FaqSectionTemplate): string => {
  if (template === 'tabs') return 'Sekmeli (Template 2)';
  if (template === 'contact-split') return 'İletişim + SSS (Template 3)';
  return 'Akordeon (Template 1)';
};

const HOMEPAGE_FAQ_POSITIONS: Array<{ value: string; label: string }> = [
  { value: 'hero', label: "Hero'nun altında" },
  { value: 'categories', label: 'Kategorilerden sonra' },
  { value: 'end', label: 'Sayfa içeriğinin sonunda' },
  { value: 'before-footer', label: "Footer'ın hemen üstünde" }
];

/** Seçilen sayfaya göre SSS konumu. Footer üstü her sayfada vardır. */
export const getFaqPositionOptions = (targetPage: string): Array<{ value: string; label: string }> => {
  if (!targetPage || targetPage === 'homepage') return HOMEPAGE_FAQ_POSITIONS;
  const pageOptions = getCorporatePagePositionOptions(targetPage);
  const options = pageOptions.length > 0 ? [...pageOptions] : [];
  if (!options.some((option) => option.value === 'before-footer')) {
    options.push({ value: 'before-footer', label: "Footer'ın hemen üstünde" });
  }
  return options;
};

export const resolveFaqPositionLabel = (targetPage: string, insertAfter?: string): string => {
  const value = String(insertAfter || 'before-footer').trim() || 'before-footer';
  const match = getFaqPositionOptions(targetPage).find((option) => option.value === value);
  if (match) return match.label;
  if (isPageContentPlacement(value)) return PAGE_CONTENT_PLACEMENT_LABELS[value];
  if (value === 'before-footer') return "Footer'ın hemen üstünde";
  return 'Özel konum';
};

const cleanText = (value: unknown): string => String(value ?? '').trim();

export type CleanFaqSectionOptions = {
  /** Düzenleme taslağını koru: boş/eksik soru-cevap ve kategori adlarını silme. */
  keepIncomplete?: boolean;
};

const cleanCategories = (raw: unknown, keepIncomplete = false): FaqSectionCategory[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      const id = cleanText(item?.id) || `cat_${index}`;
      const name = cleanText(item?.name);
      if (!name && !keepIncomplete) return null;
      return {
        id,
        name: name || `Kategori ${index + 1}`,
        enabled: item?.enabled !== false,
        order: Number(item?.order) || index
      } satisfies FaqSectionCategory;
    })
    .filter(Boolean) as FaqSectionCategory[];
};

const cleanItems = (raw: unknown, keepIncomplete = false): FaqSectionItem[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      const question = cleanText(item?.question);
      const answer = String(item?.answer ?? '').trim();
      if (!keepIncomplete && (!question || isRichTextEmpty(answer))) return null;
      return {
        id: cleanText(item?.id) || `faq_${index}`,
        question,
        answer,
        categoryId: cleanText(item?.categoryId) || undefined,
        enabled: item?.enabled !== false,
        order: Number(item?.order) || index
      } satisfies FaqSectionItem;
    })
    .filter(Boolean) as FaqSectionItem[];
};

const cleanTitleColor = (value: unknown): string => {
  const color = String(value ?? '').trim();
  if (!color) return '';
  // Metin rengi için solid hex tercih; gradient gelirse olduğu gibi saklanır
  return color;
};

const cleanContacts = (raw: unknown): FaqContactItem[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      const label = cleanText(item?.label);
      const value = cleanText(item?.value);
      if (!label && !value) return null;
      return {
        id: cleanText(item?.id) || `contact_${index}`,
        label,
        value
      } satisfies FaqContactItem;
    })
    .filter(Boolean)
    .slice(0, 12) as FaqContactItem[];
};

const cleanSettings = (raw: unknown): FaqSectionSettings => {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const template = normalizeFaqSectionTemplate(src.template);
  const settings: FaqSectionSettings = {
    template,
    colorMode: 'simple',
    titleColor: cleanTitleColor(src.titleColor)
  };
  if (template !== 'contact-split') return settings;
  return {
    ...settings,
    accentColor: cleanText(src.accentColor) || DEFAULT_CONTACT_SPLIT_SETTINGS.accentColor,
    sectionBackground: cleanText(src.sectionBackground) || DEFAULT_CONTACT_SPLIT_SETTINGS.sectionBackground,
    ctaBackground: cleanText(src.ctaBackground) || DEFAULT_CONTACT_SPLIT_SETTINGS.ctaBackground,
    leftTitle: cleanText(src.leftTitle),
    leftAccent: cleanText(src.leftAccent),
    leftDescription: cleanText(src.leftDescription),
    contacts: cleanContacts(src.contacts),
    hoursLabel: cleanText(src.hoursLabel),
    hoursValue: cleanText(src.hoursValue),
    ownerLabel: cleanText(src.ownerLabel),
    ownerValue: cleanText(src.ownerValue),
    ownerUrl: cleanText(src.ownerUrl),
    rightTitle: cleanText(src.rightTitle),
    rightAccent: cleanText(src.rightAccent),
    ctaTitle: cleanText(src.ctaTitle),
    ctaText: cleanText(src.ctaText),
    ctaLabel: cleanText(src.ctaLabel),
    ctaUrl: cleanText(src.ctaUrl)
  };
};

const FAQ_TAB_PREFIXES = [
  'about-tab:',
  'services-tab:',
  'product-solution-tab:',
  'reference-tab:',
  'custom-page:'
] as const;

export const isKnownFaqSectionTargetPage = (value: string): value is FaqSectionTargetPage =>
  value === 'homepage' ||
  value === 'about' ||
  value === 'services' ||
  value === 'products' ||
  value === 'products-solutions' ||
  value === 'references' ||
  value === 'contact' ||
  FAQ_TAB_PREFIXES.some((prefix) => value.startsWith(prefix));

/** Alt sekme / sekme hedefleri (sayfa footer hedefleri hariç) */
export const isFaqSectionTabTarget = (value: string): boolean =>
  ['about-tab:', 'services-tab:', 'product-solution-tab:', 'reference-tab:'].some((prefix) =>
    value.startsWith(prefix)
  );

export const cleanFaqSectionPayload = (
  raw: Partial<FaqSectionPayload> | null | undefined,
  options?: CleanFaqSectionOptions
): FaqSectionPayload => {
  const keepIncomplete = Boolean(options?.keepIncomplete);
  const targetRaw = cleanText(raw?.position?.targetPage) || 'homepage';
  const targetPage = isKnownFaqSectionTargetPage(targetRaw) ? targetRaw : 'homepage';
  const insertAfter = cleanText(raw?.position?.insertAfter) || 'before-footer';

  return {
    title: cleanText(raw?.title) || undefined,
    categories: cleanCategories(raw?.categories, keepIncomplete),
    items: cleanItems(raw?.items, keepIncomplete),
    settings: cleanSettings(raw?.settings || DEFAULT_FAQ_SECTION_SETTINGS),
    position: {
      targetPage,
      insertAfter
    }
  };
};
